import '@sass/_displayDirector.scss';
import { EventDispatcher, classList, isMobileView, l, logEvent, qs, qsa } from '@js/utils/toolbox';

import { eventCategories, events } from '@js/utils/event';

export class DisplayDirector {
	/**
	 * data-display (list/timetable/timeline}
	 *	 - list
	 *	 - timeline
	 *	 - timetable
	 *	 - (mobile) dateselector
	 * data-showcase (open additional modules)
	 *	 - pricecalendar
	 *	 - pricecutter
	 *	 - pricewatcher
	 */
	constructor (config) {
		this.display = 'list';
		this.showcase = config?.showcase || false;


		this.elements = {
			displayDirector: qs(`#displayDirector`),
			// loader: qs('.displayDirector__loader'),
			otherDatesChipMenu: qs('.chipMenu--otherDates'),
			changeDisplayChipMenu: qs('.chipMenu--changeDisplay'),
			priceCutterCta: qs('#pricecalendar-cta'),
			showcaseText: qs('[data-showcase-text]')
		};

		this.events();

		return this;
	}

	toggleVisible (isVisible) {
		classList(this.elements.displayDirector).toggle('displayDirector--visible', isVisible);
	}

	hideChipMenus () {
		qsa('.chipMenu').forEach((chip) => {
			chip.classList.remove('active');
		});
	}

	resetChips () {
		if (!isMobileView()) {
			return;
		}
		qsa('.chip').forEach(chip => chip.classList.remove('active'));
	}

	resetShowcase () {
		document.body.classList.remove('pricecalendar', 'pricecutter', 'pricewatcher');

		qsa(`[data-showcase]`).forEach(e => e.classList.remove('active'));

		// Only change status if showcase has been changed before
		if (isMobileView() && this.showcase && this.elements.otherDatesChipMenu) {
			this.elements.otherDatesChipMenu.dataset.status = false;
		}
	}

	logShowCaseEvent (ev, isOpen) {
		const { showcase } = ev;

		const closeEvents = {
			pricecalendar: events.closePriceCalendar,
			pricecutter: events.closePriceCutter,
			pricewatcher: events.closePriceAlert
		};

		const openEvents = {
			pricecalendar: events.openPriceCalendar,
			pricecutter: events.openPriceCutter,
			pricewatcher: events.openPriceAlert
		};

		const event = isOpen ? openEvents[showcase] : closeEvents[showcase];
		logEvent(event, eventCategories.feature);
	}

	activateShowcase (event) {
		// Force the display to be list in mobile
		if (isMobileView()) {
			this.activateDisplay({ display: 'list' });
		}

		// If the showcase is already open close it
		if (this.showcase === event.showcase) {
			this.deactivateShowcase(event);
			return;
		}

		// Analytics
		this.logShowCaseEvent(event, true);

		const { showcase } = event;

		this.showcase = showcase;

		this.resetShowcase();
		this.resetChips();

		qsa(`[data-showcase=${this.showcase}`).forEach(e => e.classList.add('active'));
		classList('body').add(this.showcase);

		if (isMobileView()) {
			if (this.elements.changeDisplayChipMenu) {
				this.elements.changeDisplayChipMenu.dataset.status = false;
			}
			if (this.elements.otherDatesChipMenu && this.showcase !== 'pricewatcher') {
				this.elements.otherDatesChipMenu.dataset.status = 'active';
			}
		}

		classList(this.elements.priceCutterCta).add('active');
		this.toggleVisible(true);

		if (this.elements.showcaseText) {
			this.elements.showcaseText.innerText = showcase === 'pricecutter' ? l('showresult.navigation.priceCutter') : l('showresult.navigation.priceCalendar');
		}

		EventDispatcher.dispatch('displayDirector:showcase:open', event);
	}

	deactivateShowcase (event) {
		this.resetShowcase();
		this.resetChips();
		this.toggleVisible(false);
		this.logShowCaseEvent(event, false);

		if (this.elements.changeDisplayChipMenu) {
			this.elements.changeDisplayChipMenu.dataset.status = 'active';
		}

		classList(this.elements.priceCutterCta).remove('active');
		EventDispatcher.dispatch('displayDirector:showcase:close', event);
		this.showcase = false;
	}

	resetDisplay () {
		classList('body').remove('list', 'timetable', 'timeline', 'pricewatcher');

		// In mobile, showcases take too much of the viewport and so we reset them when changing display
		if (isMobileView()) {
			this.resetShowcase();
		}

		qsa(`[data-display]`).forEach(e => e.classList.remove('active'));
	}

	logDisplayEvent (ev) {
		const { display } = ev;

		const displayChangeEvents = {
			list: events.changedisplayList,
			timetable: events.changedisplayTimetable,
			timeline: events.changedisplayTimeline
		};

		logEvent(displayChangeEvents[display], eventCategories.changeDisplay);
	}

	activateDisplay (ev) {
		const { display } = ev;

		this.logDisplayEvent(ev);

		display ? this.display = display : this.display = false;
		this.resetDisplay();

		if (isMobileView()) {
			this.toggleVisible(false);
			this.showcase = false;
		}

		classList(this.elements.priceCutterCta).remove('active');

		if (display) {
			this.resetChips();

			qsa(`[data-display=${display}`).forEach(e => e.classList.add('active'));
			document.body.classList.add(display);

			if (isMobileView() && this.elements.changeDisplayChipMenu && this.elements.otherDatesChipMenu) {
				this.elements.changeDisplayChipMenu.dataset.status = 'active';
				this.elements.otherDatesChipMenu.dataset.status = false;
			}
		} else {
			qsa(`[data-display=list`).forEach(e => e.classList.add('active'));
		}
		EventDispatcher.dispatch('displayDirector:display', ev);
	}

	events () {
		document.body.addEventListener('click', (event) => {
			let elem = event.target;

			const showChipsElement = elem.closest('[data-action="show-chips"]');

			// Chips are only available in mobile
			if (isMobileView() && showChipsElement) {
				if (showChipsElement.classList.contains('active')) {
					showChipsElement.classList.remove('active');
				} else {
					qsa('[data-action="show-chips"]').forEach(chip => chip.classList.remove('active'));
					showChipsElement.classList.add('active');
				}
			}

			// list / timetable / timeline
			if (elem.closest('[data-display]:not(.disabled)')) {
				elem = elem.closest('[data-display]');
				this.activateDisplay({ display: elem.dataset.display });
				return;
			}

			// Hide showcase
			// pricewatcher / pricecutter / pricecalendar
			if (elem.closest('[data-showcase]:not(.disabled).active')) {
				elem = elem.closest('[data-showcase]');
				this.deactivateShowcase({ showcase: elem.dataset.showcase });
				return;
			}

			// Show showcase
			// pricewatcher / pricecalendar / pricecutter
			if (elem.closest('[data-showcase]:not(.disabled)')) {
				elem = elem.closest('[data-showcase]');
				this.activateShowcase({ showcase: elem.dataset.showcase });
			}
		});

		EventDispatcher.addEventListener('pricealert:close', () => this.deactivateShowcase({ showcase: 'pricewatcher' }));
	}
}
